/**
 * Single source of truth for the therapy-approach pages.
 *
 * Used by:
 *  - therapist profiles (`therapyTypes` chips link to the matching page)
 *  - condition pages + the Sanity topic template ("Abordări care pot ajuta")
 *  - the approach pages themselves ("Abordări înrudite" / "Pentru ce se folosește")
 *  - the footer column
 *
 * `blurb` describes what an approach *works with*, never what it achieves:
 * no efficacy or outcome claims (COPSI).
 */

export type Approach = {
  /** Route, also the id used in `related`. */
  slug: string;
  /** Full Romanian name, for prose and chips. */
  label: string;
  /** Short link label (2–6 words). */
  short: string;
  /** Backend `therapyTypes` keys that map to this page. */
  specializationKeys: string[];
  /** Topic / condition page slugs this approach is commonly used for. */
  forTopics: string[];
  /** Sibling approaches, most closely related first. */
  related: string[];
  /** One neutral sentence about how the approach works. */
  blurb: string;
};

export const APPROACHES: Approach[] = [
  {
    slug: "/terapie-cognitiv-comportamentala",
    label: "Terapia cognitiv-comportamentală (CBT)",
    short: "Terapie cognitiv-comportamentală (CBT)",
    specializationKeys: ["cbt_tcc"],
    forTopics: [
      "anxietate",
      "anxietate-sociala",
      "depresie",
      "tulburare-de-panica",
      "burnout",
      "adhd",
      "terapie-pentru-copii-si-adolescenti",
      "insomnie",
      "fobii",
      "ganduri-intruzive",
      "procrastinare",
      "anxietate-pentru-sanatate",
      "derealizare-depersonalizare",
      "anhedonie-si-apatie",
      "paralizie-in-somn",
      "overthinking",
    ],
    related: [
      "/terapie-act",
      "/terapie-dialectic-comportamentala",
      "/terapia-schemelor",
    ],
    blurb:
      "Lucrează structurat cu gândurile și comportamentele care întrețin disconfortul, cu exerciții între ședințe.",
  },
  {
    slug: "/terapie-dialectic-comportamentala",
    label: "Terapia dialectic-comportamentală (DBT)",
    short: "Terapie dialectic-comportamentală (DBT)",
    specializationKeys: ["dbt"],
    forTopics: ["stima-de-sine", "anxietate", "tulburare-borderline"],
    related: [
      "/terapie-cognitiv-comportamentala",
      "/terapie-act",
      "/terapia-schemelor",
    ],
    blurb:
      "Combină acceptarea cu schimbarea și antrenează abilități concrete de reglare emoțională și toleranță la disconfort.",
  },
  {
    slug: "/terapie-act",
    label: "Terapia de acceptare și angajament (ACT)",
    short: "Terapie ACT",
    specializationKeys: ["act"],
    forTopics: [
      "anxietate",
      "anxietate-sociala",
      "burnout",
      "depresie",
      "adhd",
      "stres",
      "anhedonie-si-apatie",
      "anxietate-pentru-sanatate",
      "overthinking",
    ],
    related: [
      "/terapie-cognitiv-comportamentala",
      "/terapie-dialectic-comportamentala",
      "/terapie-centrata-pe-emotii",
    ],
    blurb:
      "Te ajută să faci loc emoțiilor dificile în loc să lupți cu ele și să alegi acțiuni ghidate de valorile tale.",
  },
  {
    slug: "/terapie-interpersonala",
    label: "Terapia interpersonală (IPT)",
    short: "Terapie interpersonală (IPT)",
    specializationKeys: ["ipt"],
    forTopics: ["depresie", "relatii-si-cuplu", "dupa-divort", "tulburare-bipolara", "doliu"],
    related: [
      "/terapie-cognitiv-comportamentala",
      "/terapie-sistemica",
      "/terapie-centrata-pe-emotii",
    ],
    blurb:
      "Se concentrează pe relațiile din prezent: conflicte, pierderi și tranziții de rol care apasă asupra stării tale.",
  },
  {
    slug: "/terapie-psihodinamica",
    label: "Terapia psihodinamică",
    short: "Terapie psihodinamică",
    specializationKeys: ["psychodynamic"],
    forTopics: ["depresie", "stima-de-sine", "relatii-si-cuplu", "tulburare-borderline", "narcisism", "stiluri-de-atasament"],
    related: ["/psihanaliza", "/terapie-jungiana", "/terapia-schemelor"],
    blurb:
      "Explorează tiparele emoționale formate în relațiile timpurii și felul în care ele revin în viața de acum.",
  },
  {
    slug: "/terapie-centrata-pe-emotii",
    label: "Terapia centrată pe emoții (EFT)",
    short: "Terapie centrată pe emoții (EFT)",
    specializationKeys: ["eft"],
    forTopics: ["relatii-si-cuplu", "dupa-divort", "depresie", "stiluri-de-atasament", "codependenta", "trauma-bonding-si-dinamici-toxice"],
    related: [
      "/terapie-sistemica",
      "/terapie-interpersonala",
      "/terapie-act",
    ],
    blurb:
      "Lucrează cu emoția din spatele conflictului și cu nevoia de atașament pe care partenerii nu reușesc să și-o spună.",
  },
  {
    slug: "/hipnoterapie",
    label: "Hipnoterapia",
    short: "Hipnoterapie",
    specializationKeys: ["hypnosis"],
    forTopics: ["anxietate", "tulburare-de-panica"],
    related: [
      "/terapie-cognitiv-comportamentala",
      "/terapie-emdr",
      "/terapie-psihodinamica",
    ],
    blurb:
      "Folosește o stare de relaxare focalizată pentru a lucra cu reacții automate, în cadru clinic și cu acordul tău.",
  },
  {
    slug: "/terapie-emdr",
    label: "EMDR",
    short: "EMDR",
    specializationKeys: ["emdr"],
    forTopics: ["trauma-si-ptsd", "tulburare-de-panica", "abuz-emotional", "derealizare-depersonalizare", "trauma-bonding-si-dinamici-toxice"],
    related: ["/terapie-iopt", "/terapia-schemelor", "/terapie-psihodinamica"],
    blurb:
      "Protocol structurat în opt faze care folosește stimularea bilaterală pentru reprocesarea amintirilor traumatice.",
  },
  {
    slug: "/terapie-iopt",
    label: "Terapia centrată pe traumă (IoPT)",
    short: "Terapie IoPT",
    specializationKeys: ["iopt"],
    forTopics: ["trauma-si-ptsd", "stima-de-sine"],
    related: ["/terapie-emdr", "/terapia-schemelor", "/terapie-psihodinamica"],
    blurb:
      "Abordează trauma de identitate și părțile din tine rămase blocate în experiențe timpurii.",
  },
  {
    slug: "/analiza-tranzactionala",
    label: "Analiza tranzacțională",
    short: "Analiză tranzacțională",
    specializationKeys: ["transactional_analysis"],
    forTopics: ["relatii-si-cuplu", "stima-de-sine", "trauma-bonding-si-dinamici-toxice"],
    related: [
      "/terapie-psihodinamica",
      "/psihoterapie-integrativa",
      "/terapie-sistemica",
    ],
    blurb:
      "Descrie stările eului (Părinte, Adult, Copil) și tiparele de comunicare care se repetă în relațiile tale.",
  },
  {
    slug: "/terapie-sistemica",
    label: "Terapia sistemică de familie și cuplu",
    short: "Terapie sistemică",
    specializationKeys: ["systemic_family_couple", "systemic_couple"],
    forTopics: [
      "relatii-si-cuplu",
      "dupa-divort",
      "terapie-pentru-copii-si-adolescenti",
      "tulburare-bipolara",
      "dependenta",
      "codependenta",
    ],
    related: [
      "/terapie-centrata-pe-emotii",
      "/terapie-interpersonala",
      "/analiza-tranzactionala",
    ],
    blurb:
      "Privește dificultatea la nivelul relației și al familiei, nu doar al persoanei care o resimte cel mai tare.",
  },
  {
    slug: "/psihanaliza",
    label: "Psihanaliza",
    short: "Psihanaliză",
    specializationKeys: ["psychoanalysis"],
    forTopics: ["depresie", "stima-de-sine"],
    related: [
      "/terapie-psihodinamica",
      "/terapie-jungiana",
      "/psihoterapie-integrativa",
    ],
    blurb:
      "Proces de lungă durată, centrat pe inconștient, asociere liberă și pe relația care se construiește în cabinet.",
  },
  {
    slug: "/terapie-jungiana",
    label: "Terapia jungiană",
    short: "Terapie jungiană",
    specializationKeys: ["jungian"],
    forTopics: ["depresie", "stima-de-sine"],
    related: [
      "/psihanaliza",
      "/terapie-psihodinamica",
      "/psihoterapie-integrativa",
    ],
    blurb:
      "Lucrează cu visele, simbolurile și părțile de sine neasumate, într-un proces de cunoaștere de durată.",
  },
  {
    slug: "/terapia-schemelor",
    label: "Schema Therapy",
    short: "Schema Therapy",
    specializationKeys: ["schema_therapy"],
    forTopics: ["stima-de-sine", "relatii-si-cuplu", "trauma-si-ptsd", "tulburare-borderline", "abuz-emotional", "narcisism", "stiluri-de-atasament", "codependenta", "trauma-bonding-si-dinamici-toxice"],
    related: [
      "/terapie-cognitiv-comportamentala",
      "/terapie-dialectic-comportamentala",
      "/terapie-emdr",
    ],
    blurb:
      "Identifică schemele formate în copilărie și modurile în care ele se activează azi, cu tehnici experiențiale.",
  },
  {
    slug: "/psihoterapie-integrativa",
    label: "Psihoterapia integrativă",
    short: "Psihoterapie integrativă",
    specializationKeys: ["integrative_psychotherapy"],
    forTopics: [
      "anxietate",
      "depresie",
      "stima-de-sine",
      "burnout",
      "stres",
      "sindromul-impostorului",
    ],
    related: [
      "/analiza-tranzactionala",
      "/terapie-psihodinamica",
      "/hipnoterapie",
    ],
    blurb:
      "Combină tehnici din mai multe școli și le adaptează la ritmul și nevoile fiecărei persoane.",
  },
];

/** Route → approach. */
export const APPROACH_BY_SLUG: Record<string, Approach> = Object.fromEntries(
  APPROACHES.map((a) => [a.slug, a]),
);

/** Backend `therapyTypes` key → approach page. */
export const APPROACH_BY_SPECIALIZATION: Record<string, Approach> =
  Object.fromEntries(
    APPROACHES.flatMap((a) => a.specializationKeys.map((k) => [k, a])),
  );

/** Human labels for the condition/topic pages referenced by `forTopics`. */
export const TOPIC_LABELS: Record<string, string> = {
  anxietate: "Anxietate",
  "anxietate-sociala": "Anxietate socială",
  depresie: "Depresie",
  burnout: "Burnout",
  "burnout-la-locul-de-munca": "Burnout la locul de muncă",
  "tulburare-de-panica": "Tulburare de panică",
  "trauma-si-ptsd": "Traumă și PTSD",
  "stima-de-sine": "Stimă de sine",
  "relatii-si-cuplu": "Relații și cuplu",
  "terapie-pentru-copii-si-adolescenti": "Copii și adolescenți",
  "dupa-divort": "După divorț",
  adhd: "ADHD",
  "tulburare-bipolara": "Tulburare bipolară",
  "tulburare-borderline": "Tulburare borderline",
  insomnie: "Insomnie",
  doliu: "Doliu",
  stres: "Stres",
  procrastinare: "Procrastinare",
  "sindromul-impostorului": "Sindromul impostorului",
  fobii: "Fobii",
  "ganduri-intruzive": "Gânduri intruzive",
  "abuz-emotional": "Abuz emoțional",
  "manipulare-emotionala": "Manipulare emoțională",
  dependenta: "Dependențe",
  "autism-la-adulti": "Autism la adulți",
  "stiluri-de-atasament": "Stiluri de atașament",
  narcisism: "Narcisism",
  "anhedonie-si-apatie": "Anhedonie și apatie",
  "anxietate-pentru-sanatate": "Anxietate pentru sănătate",
  "trauma-bonding-si-dinamici-toxice": "Trauma bonding",
  "derealizare-depersonalizare": "Derealizare și depersonalizare",
  codependenta: "Codependență",
  "paralizie-in-somn": "Paralizie în somn",
  overthinking: "Overthinking (ruminație)",
  "constelatii-familiale": "Constelații familiale",
  "cum-le-spui-celor-dragi": "Cum le spui celor dragi",
  "poti-singur-fara-terapeut": "Poți singur, fără terapeut?",
  "terapie-de-cuplu-online": "Terapie de cuplu online",
  "terapie-gestalt": "Terapia gestalt",
};

/**
 * Sibling condition pages, for the Sanity topic template. The static condition
 * pages link each other from their body copy; the Sanity-driven ones cannot,
 * because their body is editor-owned, so the template reads this map instead.
 */
export const RELATED_TOPICS: Record<string, string[]> = {
  anxietate: ["anxietate-sociala", "tulburare-de-panica", "anxietate-pentru-sanatate", "ganduri-intruzive", "overthinking", "fobii"],
  "anxietate-sociala": ["anxietate", "fobii", "stima-de-sine", "sindromul-impostorului", "autism-la-adulti"],
  "tulburare-de-panica": ["anxietate", "fobii", "derealizare-depersonalizare", "paralizie-in-somn"],
  fobii: ["tulburare-de-panica", "anxietate-sociala", "ganduri-intruzive", "anxietate"],
  "ganduri-intruzive": ["anxietate", "fobii", "anxietate-pentru-sanatate", "tulburare-de-panica", "overthinking"],
  "anxietate-pentru-sanatate": ["anxietate", "ganduri-intruzive", "tulburare-de-panica", "stres"],
  depresie: ["anhedonie-si-apatie", "tulburare-bipolara", "insomnie", "poti-singur-fara-terapeut"],
  "anhedonie-si-apatie": ["depresie", "burnout", "insomnie", "stres"],
  "tulburare-bipolara": ["depresie", "tulburare-borderline", "insomnie", "cum-le-spui-celor-dragi"],
  "tulburare-borderline": ["tulburare-bipolara", "abuz-emotional", "stiluri-de-atasament", "trauma-si-ptsd"],
  burnout: ["stres", "insomnie", "sindromul-impostorului", "procrastinare", "anhedonie-si-apatie"],
  "burnout-la-locul-de-munca": ["burnout", "stres", "sindromul-impostorului"],
  stres: ["burnout", "insomnie", "anxietate", "poti-singur-fara-terapeut", "overthinking"],
  insomnie: ["stres", "paralizie-in-somn", "depresie", "anxietate", "overthinking"],
  overthinking: ["ganduri-intruzive", "anxietate", "insomnie", "depresie"],
  "paralizie-in-somn": ["insomnie", "tulburare-de-panica", "trauma-si-ptsd", "derealizare-depersonalizare"],
  procrastinare: ["adhd", "sindromul-impostorului", "stima-de-sine", "anxietate", "overthinking"],
  "sindromul-impostorului": ["stima-de-sine", "procrastinare", "burnout", "anxietate-sociala"],
  "stima-de-sine": ["sindromul-impostorului", "anxietate-sociala", "codependenta", "terapie-gestalt"],
  adhd: ["procrastinare", "autism-la-adulti", "stima-de-sine", "anxietate"],
  "autism-la-adulti": ["adhd", "anxietate-sociala", "stres", "cum-le-spui-celor-dragi"],
  dependenta: ["codependenta", "depresie", "anxietate", "cum-le-spui-celor-dragi"],
  codependenta: ["relatii-si-cuplu", "stiluri-de-atasament", "dependenta", "stima-de-sine"],
  "relatii-si-cuplu": ["terapie-de-cuplu-online", "stiluri-de-atasament", "dupa-divort", "codependenta"],
  "terapie-de-cuplu-online": ["relatii-si-cuplu", "dupa-divort", "stiluri-de-atasament", "codependenta"],
  "dupa-divort": ["relatii-si-cuplu", "doliu", "terapie-de-cuplu-online", "stiluri-de-atasament"],
  "stiluri-de-atasament": ["relatii-si-cuplu", "codependenta", "terapie-de-cuplu-online", "trauma-bonding-si-dinamici-toxice"],
  "abuz-emotional": ["manipulare-emotionala", "trauma-bonding-si-dinamici-toxice", "narcisism", "trauma-si-ptsd"],
  "manipulare-emotionala": ["abuz-emotional", "narcisism", "trauma-bonding-si-dinamici-toxice", "relatii-si-cuplu"],
  narcisism: ["manipulare-emotionala", "abuz-emotional", "trauma-bonding-si-dinamici-toxice", "tulburare-borderline"],
  "trauma-bonding-si-dinamici-toxice": ["abuz-emotional", "manipulare-emotionala", "narcisism", "codependenta"],
  "trauma-si-ptsd": ["abuz-emotional", "derealizare-depersonalizare", "doliu", "constelatii-familiale"],
  "derealizare-depersonalizare": ["tulburare-de-panica", "anxietate", "trauma-si-ptsd", "paralizie-in-somn"],
  doliu: ["dupa-divort", "depresie", "cum-le-spui-celor-dragi", "constelatii-familiale"],
  "poti-singur-fara-terapeut": ["cum-le-spui-celor-dragi", "stres", "depresie", "terapie-gestalt"],
  "cum-le-spui-celor-dragi": ["poti-singur-fara-terapeut", "depresie", "anxietate", "dependenta"],
  "constelatii-familiale": ["terapie-gestalt", "doliu", "trauma-si-ptsd", "relatii-si-cuplu"],
  "terapie-gestalt": ["constelatii-familiale", "stima-de-sine", "anxietate", "relatii-si-cuplu"],
  "terapie-pentru-copii-si-adolescenti": ["anxietate", "adhd", "autism-la-adulti", "cum-le-spui-celor-dragi"],
};

/** Normalises "/anxietate", "anxietate" and "anxietate/" to "anxietate". */
function normaliseTopic(topic: string) {
  return topic.replace(/^\/+|\/+$/g, "");
}

/** Approaches commonly used for a condition page, capped at `limit`. */
export function approachesForTopic(topic: string, limit = 3): Approach[] {
  const slug = normaliseTopic(topic);
  return APPROACHES.filter((a) => a.forTopics.includes(slug)).slice(0, limit);
}

/** Sibling condition pages for a topic. */
export function relatedTopics(
  topic: string,
): { slug: string; href: string; label: string }[] {
  const slug = normaliseTopic(topic);
  return (RELATED_TOPICS[slug] ?? [])
    .filter((t) => TOPIC_LABELS[t])
    .map((t) => ({ slug: t, href: `/${t}`, label: TOPIC_LABELS[t] }));
}

/** The sibling approaches listed at the end of an approach page. */
export function relatedApproaches(slug: string): Approach[] {
  const approach = APPROACH_BY_SLUG[slug];
  if (!approach) return [];
  return approach.related
    .map((s) => APPROACH_BY_SLUG[s])
    .filter((a): a is Approach => Boolean(a));
}

/** Condition pages an approach page should link out to. */
export function topicsForApproach(
  slug: string,
): { slug: string; href: string; label: string }[] {
  const approach = APPROACH_BY_SLUG[slug];
  if (!approach) return [];
  return approach.forTopics
    .filter((t) => TOPIC_LABELS[t])
    .map((t) => ({ slug: t, href: `/${t}`, label: TOPIC_LABELS[t] }));
}

/**
 * Footer column. These are the approach pages that had a single inbound link
 * (from /resurse) before this pass, so the footer gives every one of them a
 * site-wide link. CBT / EMDR / psihanaliză / psihodinamică are already linked
 * from several body pages and reach the footer through "Toate abordările".
 */
export const FOOTER_APPROACH_SLUGS = [
  "/terapie-act",
  "/terapie-dialectic-comportamentala",
  "/terapie-centrata-pe-emotii",
  "/terapie-sistemica",
  "/terapia-schemelor",
  "/analiza-tranzactionala",
  "/hipnoterapie",
  "/terapie-interpersonala",
  "/terapie-iopt",
  "/terapie-jungiana",
  "/psihoterapie-integrativa",
];

/** Footer entries, resolved once. */
export const FOOTER_APPROACHES: Approach[] = FOOTER_APPROACH_SLUGS.map(
  (slug) => APPROACH_BY_SLUG[slug],
).filter((a): a is Approach => Boolean(a));
